/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type Capacity, type Command, type PromptConfigFull, type SelectOption, CAPACITY_UNIT, Consts,
    DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, TimeService, UNPLACEHOLDER,
    UtilService,
} from '@dm/core';
import { ToolService } from 'app/common/services';
import { OnAccessPolicy } from 'app/common/types/mo';
import { merge } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { AntivirusService } from '../../../antivirus.service';
import { AntivirusStrService, DurationTypeE, ScanTriggerE, ScheduleTypeE } from '../../../antivirus-str.service';

export interface PolicyVm {
    /**
     * 策略名称
     */
    name?: string;
    /**
     * 不扫描文件类型
     */
    excludeFileType?: string[];
    /**
     * 是否限制不扫描文件路径
     */
    isNeedExcludePeriod?: boolean;
    /**
     * 不扫描文件路径
     */
    excludeFilePath?: string[];
    /**
     * 是否限制最大可扫描文件大小
     */
    restricted?: boolean;
    /**
     * 最大可扫描文件大小容量
     */
    restrictCapacity?: Capacity;
    /**
     * 超时时间
     */
    timeout?: number;
    /**
     * 超时后是否可访问
     */
    accessAfterTimeout?: boolean;
    /**
     * 触发时机
     */
    scanTrigger?: string;
}
@Component({
    selector: 'app-create-on-access-scan',
    templateUrl: './create-on-access-scan.component.html',
    styleUrls: ['./create-on-access-scan.component.scss'],
})
export class CreateOnAccessScanComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() policyData: OnAccessPolicy;
    @Input() vstoreId: string;

    i18n = this.str.i18n;
    ScheduleTypeE = ScheduleTypeE;
    DurationTypeE = DurationTypeE;

    modifyVmValue: PolicyVm;
    vm: FormGroup;
    errorMessage: any;
    maxScanCapacityUnit: string[] = this.str.maxScanCapacityUnit;
    scanTriggerList = this.str.scanTriggerList;
    excludePeriodList: string[] = [];
    excludePeriodObjList: SelectOption[] = [];
    // 文件扫描最小限制
    fileSizeMin = '1 MB';
    // 文件扫描最大限制
    fileSizeMax = '4 GB';
    // 名称长度
    nameMinLen = 1;
    nameMaxLen = 127;
    // 单个不扫描文件类型长度
    fileTypeMinLen = 1;
    fileTypeMaxLen = 8;
    // 侧滑框标题
    title = this.i18n.createOnAccessPolicy;
    fileTypeSeperator = [',', ';'];
    filePathSeperator = this.str.filePathSeperator;
    excludePeriodErrTip: string = null;

    closeArgs = {
        refresh: false,
        data: null,
    };


    constructor(
        injector: Injector,
        private str: AntivirusStrService,
        private service: AntivirusService,
        private toolService: ToolService,
        private utilService: UtilService,
        private timeService: TimeService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        // 修改入口初始化参数
        if (this.policyData) {
            this.initModifyValue();
            this.title = this.i18n.modifyOnAccessPolicy;
        }

        this.checkVmDisabled();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        this.vm = new FormGroup({
            name: new FormControl('', {
                validators: [
                    Validators.required,
                    DMValidators.asciiName,
                    DMValidators.rangeByteLength(this.nameMinLen, this.nameMaxLen),
                ],
            }),
            excludeFileType: new FormControl([], {
                validators: [
                    DMValidators.multiInputValidator(false, [
                        DMValidators.rangeByteLength(this.fileTypeMinLen, this.fileTypeMaxLen),
                        this.str.fileTypeValidator,
                    ]),
                    this.str.fileTypeNumValidator,
                    this.str.fileTypeLengthValidator,
                ],
            }),
            isNeedExcludePeriod: new FormControl(false),
            excludeStartTime: new FormControl('00:00'),
            excludeEndTime: new FormControl('00:00'),
            excludeFilePath: new FormControl([], {
                validators: [
                    DMValidators.multiInputValidator(false, [
                        DMValidators.rangeByteLength(1, this.str.filePathMaxLen),
                        this.str.filePathValidator,
                    ]),
                    this.str.filePathNumValidator,
                    this.str.filePathLengthValidator,
                ],
            }),
            restricted: new FormControl(true),
            restrictCapacity: new FormControl({ value: 512, unit: Consts.MB }, {
                validators: [
                    DMValidators.capacityValidator(
                        true,
                        this.fileSizeMin,
                        this.fileSizeMax,
                    ),
                ],
            }),
            timeout: new FormControl(5),
            accessAfterTimeout: new FormControl(true),
            scanTrigger: new FormControl(ScanTriggerE.CLOSE),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            name: {
                required: this.toolService.requiredErr(this.i18n.name),
                asciiName: this.toolService.asciiNameErr(this.i18n.name),
                rangeByteLength: this.toolService.rangeByteLengthErr(this.nameMinLen, this.nameMaxLen),
            },
            excludeFileType: {
                fileTypeName: this.i18n.fileTypeNameErr,
                rangeByteLength: this.toolService.rangeByteLengthErr(this.fileTypeMinLen, this.fileTypeMaxLen),
                fileTypeNum: this.i18n.fileTypeNumErr,
                fileTypeLength: this.i18n.fileTypeLengthErr,
            },
            excludeFilePath: {
                filePathName: this.i18n.filePathNameErr,
                rangeByteLength: this.toolService.rangeByteLengthErr(1, this.str.filePathMaxLen),
                filePathNum: this.i18n.filePathNumErr,
                filePathLength: this.i18n.filePathLengthErr,
            },
            restrictCapacity: {
                required: this.i18n.capacityRangeErr(this.fileSizeMin, this.fileSizeMax),
                min: this.i18n.capacityRangeErr(this.fileSizeMin, this.fileSizeMax),
                max: this.i18n.capacityRangeErr(this.fileSizeMin, this.fileSizeMax),
            },
        };

        this.vm.get('excludeStartTime').valueChanges.subscribe((newValue) => {
            const oldValue = this.vm.value.excludeStartTime;

            // 首次展示表单项时，不触发校验
            if (!oldValue) {
                return;
            }

            if (newValue === oldValue) {
                return;
            }

            this.checkExcludePeriodValid();
        });
        this.vm.get('excludeEndTime').valueChanges.subscribe((newValue) => {
            const oldValue = this.vm.value.excludeEndTime;

            // 首次展示表单项时，不触发校验
            if (!oldValue) {
                return;
            }

            if (newValue === oldValue) {
                return;
            }

            this.checkExcludePeriodValid();
        });

        this.detectTypeChange();
    }

    /**
     * 检测文件类型变化
     */
    detectTypeChange(): void {
        merge(
            this.vm.get('excludeFileType').valueChanges.pipe(distinctUntilChanged()),
        ).subscribe(() => {
            const fileType = this.vm.get('excludeFileType').value;
            if (/\s+/.test(fileType)) {
                const newFileType = fileType.map((item: string) => item.replace(/\s+/g, ''))
                    .filter((str: string) => Boolean(str));
                this.vm.get('excludeFileType').setValue(Array.from(new Set(newFileType)));
            }
        });
    }

    /**
     * 添加不扫描时间段
     */
    addExcludePeriod(): void {
        if (!this.checkExcludePeriodValid()) {
            return;
        }

        const startTime = this.vm.get('excludeStartTime').value;
        const endTime = this.vm.get('excludeEndTime').value;

        this.excludePeriodList.push(`${startTime} - ${endTime}`);

        this.updateExcludePeriodList();
    }

    /**
     * 删除不扫描时间段
     */
    deleteExcludePeriod(excludePeriod: string): void {
        this.excludePeriodList.splice(this.excludePeriodList.findIndex((item) => item === excludePeriod), 1);
        if (!this.checkExcludePeriodListValid()) {
            return;
        }
    }

    // 更新不扫描时段
    updateExcludePeriodList(): void {
        this.excludePeriodObjList = (this.excludePeriodList || []).map((period, index) => ({
            label: period,
            value: index,
        }));
    }

    // 移除不扫描时段
    removePeriod(item?: SelectOption): void {
        if (!item) {
            this.excludePeriodList = [];
        } else {
            this.excludePeriodList = this.excludePeriodList.filter((period) => period !== item.label);
        }
        this.updateExcludePeriodList();

        if (!this.checkExcludePeriodListValid()) {
            return;
        }

        this.checkExcludePeriodValid();
    }

    /**
     * 初始化修改页面参数
     */
    initModifyValue(): void {
        const { value, unit } = Number.isInteger(this.utilService.adaptUnit(this.policyData.maxFileSize).value)
            ? this.utilService.adaptUnit(this.policyData.maxFileSize)
            : this.utilService.adaptUnit(this.policyData.maxFileSize, undefined, CAPACITY_UNIT.MB);

        const vmValue: PolicyVm = {
            name: this.policyData.name,
            excludeFileType: this.policyData.excludeFileType === UNPLACEHOLDER ? [] : this.policyData.excludeFileType.split(','),
            excludeFilePath: this.policyData.excludeFilePath === UNPLACEHOLDER
                ? []
                : this.str.getExcludeFilePathVmValue(this.policyData.excludeFilePath),
            restricted: value !== 0,
            restrictCapacity: value === 0 ? { value: undefined, unit: CAPACITY_UNIT.MB } : { value, unit },
            timeout: Number(this.policyData.timeout),
            accessAfterTimeout: this.policyData.accessAfterTimeout === '1',
            scanTrigger: String(this.policyData.scanTrigger),
            isNeedExcludePeriod: this.policyData.excludePeriod !== UNPLACEHOLDER,
        };

        this.excludePeriodList = this.policyData.excludePeriod === UNPLACEHOLDER
            ? []
            : this.policyData.excludePeriod.split(',').map((item) => item.replace(/\-/g, ' - '));

        this.updateExcludePeriodList();

        Object.keys(vmValue).forEach((item) => {
            this.vm.get(item).setValue(vmValue[item]);
        });

        this.vm.restore();
    }

    /**
     * vm灰化亮化
     */
    checkVmDisabled(): void {
        const vm = this.vm;
        const vmValue = vm.getRawValue();

        if (vmValue.restricted) {
            vm.get('restrictCapacity').enable();
        } else {
            vm.get('restrictCapacity').disable();
        }

        if (vmValue.isNeedExcludePeriod) {
            vm.get('excludeStartTime').enable();
            vm.get('excludeEndTime').enable();
        } else {
            vm.get('excludeStartTime').disable();
            vm.get('excludeEndTime').enable();
        }
    }

    /**
     * needErrTip：是否需要复制错误提示内容
     */
    checkExcludePeriodValid(needErrTip = true): boolean {
        this.excludePeriodErrTip = null;
        if (!this.vm.value.isNeedExcludePeriod) {
            return true;
        }

        const time = `${this.vm.get('excludeStartTime').value} - ${this.vm.get('excludeEndTime').value}`;
        const startMoment = this.timeService.moment(this.vm.get('excludeStartTime').value, 'HH:mm');
        const endMoment = this.timeService.moment(this.vm.get('excludeEndTime').value, 'HH:mm');

        // 最大可下发5个时间段
        const isMaxLength = this.excludePeriodList.length === 5;
        // 时间段不能重复
        const hasRepeat = this.excludePeriodList.findIndex((item) => item === time) !== -1;
        // 开始时间不能大于等于结束时间
        const startOverEnd = startMoment >= endMoment;
        // 时间段不能有交集
        const hasIntersections = this.excludePeriodList.some((item) => {
            const [existStartTime, existEndTime] = item.split(' - ');
            const existStartTimeMom = this.timeService.moment(existStartTime, 'HH:mm');
            const existEndTimeMom = this.timeService.moment(existEndTime, 'HH:mm');

            return existStartTimeMom.isBetween(startMoment, endMoment, null)
                || existEndTimeMom.isBetween(startMoment, endMoment, null)
                || startMoment.isBetween(existStartTimeMom, existEndTimeMom, null)
                || endMoment.isBetween(existStartTimeMom, existEndTimeMom, null);
        });

        // 不需要赋值错误提示内容
        if (!needErrTip) {
            return !(isMaxLength || hasRepeat || startOverEnd || hasIntersections);
        }

        if (isMaxLength) {
            this.excludePeriodErrTip = this.i18n.excludePeriodNumErr;
            return false;
        }

        if (hasRepeat) {
            this.excludePeriodErrTip = this.i18n.excludePeriodSameErr;
            return false;
        }

        if (startOverEnd) {
            this.excludePeriodErrTip = this.i18n.excludePeriodDateCompareErr;
            return false;
        }

        if (hasIntersections) {
            this.excludePeriodErrTip = this.i18n.excludePeriodIntersectErr;
            return false;
        }

        return true;
    }

    /**
     * 校验时段列表是否为空
     */
    checkExcludePeriodListValid(): boolean {
        this.excludePeriodErrTip = null;
        if (!this.vm.value.isNeedExcludePeriod) {
            return true;
        }

        const isEmpty = this.excludePeriodList.length === 0;

        if (isEmpty) {
            this.excludePeriodErrTip = this.i18n.emptyExcludePeriodErr(this.i18n.excludePeriod);
            return false;
        }
        return true;
    }

    /**
     * 调度类型和持续时间类型变化
     */
    onStrategyChange(): void {
        this.checkVmDisabled();
    }

    /**
     * 是否不勾选扫描时段变化
     */
    onExcludePeriodChange(): void {
        this.checkVmDisabled();
    }

    /**
     * 不扫描时间是否有变化
     */
    isExcludePeriodChanged(): boolean {
        const userData = this.excludePeriodList.sort().join(',').replace(/\s+/g, '');
        const sourceData = (this.policyData.excludePeriod === UNPLACEHOLDER ? [] : this.policyData.excludePeriod.split(','))
            .sort().join(',').replace(/\s+/g, '');

        if (userData === sourceData) {
            return false;
        } else {
            return true;
        }
    }

    /**
     * 获取最大扫描大小限制下发参数，下发单位为MB
     */
    getMaxFileSizeParam(): number {
        // 0视为不限制
        if (this.vm.value.restricted) {
            return this.utilService.adaptUnit(
                this.vm.value.restrictCapacity.value,
                this.vm.value.restrictCapacity.unit,
                Consts.MB,
            ).value;
        } else {
            return 0;
        }
    }

    /**
     * 高危提示
     */
    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];

        if (this.policyData) {
            if (this.vm.changedValue &&
                Object.prototype.hasOwnProperty.call(this.vm.changedValue, 'accessAfterTimeout') &&
                !this.vm.changedValue.accessAfterTimeout) {
                prompts.push({
                    id: 'prompt-antivirus-modifyOnAccessPolicyHighRiskTip',
                    opType: 'info',
                    title: this.i18n.modifyOnAccessPolicy,
                    content: this.i18n.modifyOnAccessPolicyHighRiskTip,
                    width: 500,
                });
            }
        } else {
            if (!this.vm.get('accessAfterTimeout').value) {
                prompts.push({
                    id: 'prompt-antivirus-createOnAccessPolicyHighRiskTip',
                    opType: 'info',
                    title: this.i18n.createOnAccessPolicy,
                    content: this.i18n.createOnAccessPolicyHighRiskTip,
                    width: 500,
                });
            }
        }

        return prompts;
    }

    /**
     * 获取创建下发参数
     */
    buildCreateCmds(): Command[] {
        const cmds: Command[] = [];
        const values = this.vm.value;

        const params: OnAccessPolicy = {
            name: values.name,
            excludeFileType: values.excludeFileType.join(','),
            excludePeriod: values.isNeedExcludePeriod ? this.excludePeriodList.join(',').replace(/\s+/g, '') : '',
            excludeFilePath: this.str.getExcludeFilePathParam(values.excludeFilePath),
            maxFileSize: this.getMaxFileSizeParam(),
            timeout: values.timeout,
            accessAfterTimeout: values.accessAfterTimeout ? '1' : '0',
            scanTrigger: values.scanTrigger,
            vstoreId: this.vstoreId,
        };

        cmds.push({
            name: this.i18n.createOnAccessPolicyAction(params.name),
            action: this.service.createOnAccessPolicy(params, { restNameParams: [params.name], doException: false })
                .pipe(
                    tap(({ data }) => this.closeArgs = { ...this.closeArgs, data }),
                ),
        });

        return cmds;
    }

    /**
     * 获取修改下发参数
     */
    buildModifyCmds(): Command[] {
        const cmds: Command[] = [];
        const changedValue = this.vm.changedValue;
        const params: OnAccessPolicy = {
            ID: this.policyData.ID,
        };

        if (changedValue) {
            if (Object.prototype.hasOwnProperty.call(changedValue, 'excludeFileType')) {
                if (changedValue.excludeFileType.length) {
                    params.excludeFileType = changedValue.excludeFileType.join(',');
                } else {
                    params.clearExcludeFileType = 'yes';
                }
            }

            if (Object.prototype.hasOwnProperty.call(changedValue, 'excludeFilePath')) {
                if (changedValue.excludeFilePath.length) {
                    params.excludeFilePath = this.str.getExcludeFilePathParam(changedValue.excludeFilePath);
                } else {
                    params.clearExcludeFilePath = 'yes';
                }
            }

            if (Object.prototype.hasOwnProperty.call(changedValue, 'restricted') || Object.prototype.hasOwnProperty.call(changedValue, 'restrictCapacity')) {
                params.maxFileSize = this.getMaxFileSizeParam();
            }

            if (Object.prototype.hasOwnProperty.call(changedValue, 'timeout')) {
                params.timeout = changedValue.timeout;
            }

            if (Object.prototype.hasOwnProperty.call(changedValue, 'accessAfterTimeout')) {
                params.accessAfterTimeout = changedValue.accessAfterTimeout ? '1' : '0';
            }

            if (Object.prototype.hasOwnProperty.call(changedValue, 'scanTrigger')) {
                params.scanTrigger = changedValue.scanTrigger;
            }
        }

        if (this.isExcludePeriodChanged() || Object.prototype.hasOwnProperty.call(changedValue, 'isNeedExcludePeriod')) {
            const excludePeriod = this.vm.value.isNeedExcludePeriod ? this.excludePeriodList.join(',').replace(/\s+/g, '') : '';

            if (excludePeriod === '') {
                params.clearExcludePeriod = 'yes';
            } else {
                params.excludePeriod = this.excludePeriodList.join(',').replace(/\s+/g, '');
            }
        }

        cmds.push({
            name: this.i18n.modifyOnAccessPolicyAction(this.policyData.name),
            action: this.service.modifyOnAccessPolicy(params),
        });

        return cmds;
    }

    buildCmds(): Command[] {
        let cmds: Command[] = [];

        if (this.policyData) {
            cmds = this.buildModifyCmds();
        } else {
            cmds = this.buildCreateCmds();
        }

        return cmds;
    }

    isModified(): boolean {
        const changedValue = this.vm.changedValue;

        if (changedValue) {
            delete changedValue.excludeStartTime;
            delete changedValue.excludeEndTime;
        }

        return changedValue && Boolean(Object.keys(changedValue).length) || this.isExcludePeriodChanged();
    }

    onOk(): void {
        this.validate().subscribe((valid) => {
            // 点确认的时候不下发待填项，清除代填项校验tip
            this.checkExcludePeriodValid(false);

            // 点确认校验已填项是否为空
            this.checkExcludePeriodListValid();

            if (!valid || this.excludePeriodErrTip) {
                return;
            }

            if (this.isModified()) {
                const promptConfs = this.prompt();
                if (promptConfs && promptConfs.length) {
                    this.promptService.multi(promptConfs, () => {
                        this.promptService.close();
                        this.exec();
                    });
                } else {
                    this.exec();
                }
            } else {
                this.hide();
            }
        });
    }

    /**
     * 修正持续时间，ex: 8 -> '08'，便于后续拼接下发
     */
    fixDurationValue(val: number): string {
        return (String(val)).padStart(2, '0');
    }
}
